import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TrashIcon, BuildingsIcon, CaretDownIcon, InfinityIcon, UserPlusIcon, ShieldCheckIcon, CrownSimpleIcon, LockKeyIcon, MagnifyingGlassIcon, PrinterIcon, FileXlsIcon, XIcon } from "@phosphor-icons/react";
import { PERMISSION_SECTIONS, SECTION_LABEL } from "@/lib/permissions";
import { downloadRowsAsXlsx, openPrintWindow, rowsToHtmlTable } from "@/lib/exportUtils";
import { PropertyRoles } from "@/components/PropertyRoles";

const roleColor = {
  admin: "bg-primary/15 text-primary",
  manager: "bg-secondary/15 text-secondary",
  front_desk: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  housekeeping: "bg-muted text-muted-foreground",
  restaurant_manager: "bg-[hsl(340_60%_92%)] text-[hsl(340_60%_35%)]",
  service: "bg-[hsl(200_60%_92%)] text-[hsl(200_70%_35%)]",
};

const roleLabel = {
  admin: "Admin",
  manager: "Manager",
  front_desk: "Front Desk",
  housekeeping: "Housekeeping",
  restaurant_manager: "Restaurant Manager",
  service: "Service (Waiter)",
};

function PermissionsButton({ user: staffUser, disabled, onSaved, locked }) {
  const [open, setOpen] = useState(false);
  const [defaults, setDefaults] = useState({});
  const [current, setCurrent] = useState({});
  useEffect(() => {
    if (!open) return;
    api.get("/permissions/defaults").then((r) => setDefaults(r.data.role_defaults?.[staffUser.role] || {})).catch(() => {});
    setCurrent({ ...(staffUser.permissions || {}) });
  }, [open, staffUser.id, staffUser.role, staffUser.permissions]);

  if (locked) {
    return <span className="text-[11px] flex items-center gap-1 text-gold" data-testid={`staff-perm-${staffUser.id}-master`} title="Master owner has irrevocable full access"><LockKeyIcon size={11} weight="fill" /> Locked · full access</span>;
  }
  if (staffUser.role === "admin") {
    return <span className="text-[11px] flex items-center gap-1 text-primary" data-testid={`staff-perm-${staffUser.id}-admin`}><InfinityIcon size={11} /> Full access</span>;
  }
  const overridesCount = Object.keys(current).length;
  const label = overridesCount > 0 ? `${overridesCount} override${overridesCount === 1 ? "" : "s"}` : "Role defaults";
  const setLevel = (section, level) => {
    setCurrent((c) => {
      const next = { ...c };
      if (defaults[section] === level) delete next[section]; // if same as default, drop override
      else next[section] = level;
      return next;
    });
  };
  const save = async () => {
    try {
      await api.patch(`/users/${staffUser.id}`, { permissions: current });
      toast.success("Permissions saved");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reset = () => setCurrent({});

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={disabled} data-testid={`staff-perm-btn-${staffUser.id}`}>
          <ShieldCheckIcon size={12} className="mr-1" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{staffUser.name} · section permissions</DialogTitle>
          <p className="text-xs text-muted-foreground">Grant read-only or full write access per section. Leave as the role default (blue) unless you need a per-user override.</p>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto pr-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground uppercase tracking-wider">
                <th className="text-left p-2">Section</th>
                <th className="text-center p-2">None</th>
                <th className="text-center p-2">Read only</th>
                <th className="text-center p-2">Read + Write</th>
              </tr>
            </thead>
            <tbody>
              {PERMISSION_SECTIONS.map((s) => {
                const effective = current[s] ?? defaults[s] ?? "none";
                const isOverride = current[s] !== undefined;
                return (
                  <tr key={s} className="border-t border-border/60" data-testid={`staff-perm-row-${staffUser.id}-${s}`}>
                    <td className="p-2">
                      {SECTION_LABEL[s] || s}
                      {isOverride && <span className="text-[9px] uppercase text-secondary ml-2">override</span>}
                    </td>
                    {["none", "read", "write"].map((lvl) => (
                      <td key={lvl} className="p-2 text-center">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name={`perm-${staffUser.id}-${s}`}
                            checked={effective === lvl}
                            onChange={() => setLevel(s, lvl)}
                            className={`h-4 w-4 accent-primary ${!isOverride && defaults[s] === lvl ? "opacity-60" : ""}`}
                            data-testid={`staff-perm-input-${staffUser.id}-${s}-${lvl}`}
                          />
                        </label>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={reset} data-testid={`staff-perm-reset-${staffUser.id}`}>Reset to role defaults</Button>
          <Button onClick={save} data-testid={`staff-perm-save-${staffUser.id}`}>Save permissions</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PosAccessToggle({ user: staffUser, onSave, disabled }) {
  // Admins/managers are always allowed; other roles are opt-in
  if (staffUser.role === "admin" || staffUser.role === "manager") {
    return <span className="text-[11px] flex items-center gap-1 text-primary" data-testid={`staff-pos-${staffUser.id}-always`}><InfinityIcon size={11} /> Always on</span>;
  }
  const enabled = staffUser.can_access_pos !== false;
  return (
    <label className={`flex items-center gap-2 text-xs cursor-pointer ${disabled ? "opacity-60" : ""}`} data-testid={`staff-pos-${staffUser.id}`}>
      <Checkbox
        checked={enabled}
        disabled={disabled}
        onCheckedChange={(v) => onSave(!!v)}
      />
      <span>{enabled ? "Allowed" : "Blocked"}</span>
    </label>
  );
}

function PropertyAssign({ user: staffUser, properties, onSave, disabled }) {
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState(staffUser.property_ids || []);
  useEffect(() => { setIds(staffUser.property_ids || []); }, [staffUser.id, staffUser.property_ids]);

  const toggle = (pid) => setIds((prev) => prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);
  const save = async () => {
    await onSave(ids);
    setOpen(false);
  };
  const label = staffUser.role === "admin"
    ? <span className="flex items-center gap-1 text-primary"><InfinityIcon size={12} /> All properties</span>
    : (ids.length === 0
        ? <span className="text-muted-foreground">All properties</span>
        : `${ids.length} of ${properties.length}`);

  if (disabled || staffUser.role === "admin") {
    return <div className="text-xs">{label}</div>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs justify-between min-w-[140px]" data-testid={`staff-assign-${staffUser.id}`}>
          <span className="flex items-center gap-1"><BuildingsIcon size={12} /> {label}</span>
          <CaretDownIcon size={10} className="ml-1 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <p className="label-overline mb-2">Assign properties</p>
        <p className="text-xs text-muted-foreground mb-3">Leave everything unchecked to grant access to <strong>all</strong> properties.</p>
        <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
          {properties.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded-md p-1.5" data-testid={`staff-assign-prop-${p.id}`}>
              <Checkbox
                checked={ids.includes(p.id)}
                onCheckedChange={() => toggle(p.id)}
              />
              <span className="flex-1 truncate">{p.name}</span>
              <span className="text-xs text-muted-foreground">{p.city}</span>
            </label>
          ))}
          {properties.length === 0 && <p className="text-xs italic text-muted-foreground">No properties yet.</p>}
        </div>
        <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-border">
          <Button size="sm" variant="ghost" onClick={() => { setIds([]); }}>Clear</Button>
          <Button size="sm" onClick={save} data-testid={`staff-assign-save-${staffUser.id}`}>Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Staff() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [masterEmail, setMasterEmail] = useState("");
  const load = () => {
    api.get("/users").then((r) => setItems(r.data));
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/platform/master").then((r) => setMasterEmail((r.data?.master_email || "").toLowerCase())).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const isMasterUser = (u) => !!masterEmail && (u.email || "").toLowerCase() === masterEmail;

  const setRole = async (u, role) => {
    await api.patch(`/users/${u.id}`, { role });
    toast.success("Role updated");
    load();
  };

  const setPropertyRoles = async (u, property_roles) => {
    try {
      await api.patch(`/users/${u.id}`, { property_roles });
      const n = Object.keys(property_roles).length;
      toast.success(n ? `Access set for ${n} propert${n === 1 ? "y" : "ies"}` : "Access granted to all properties");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); toast.success("Removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const isAdmin = user?.role === "admin";
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "front_desk", property_ids: [] });

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) return toast.error("Name, email and a password of 6+ characters are required");
    try {
      await api.post("/users", form);
      toast.success(`${form.name} added as ${roleLabel[form.role]}`);
      setNewOpen(false);
      setForm({ name: "", email: "", password: "", role: "front_desk", property_ids: [] });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to create staff"); }
  };

  const toggleAssign = (pid) => setForm((f) => ({
    ...f, property_ids: f.property_ids.includes(pid) ? f.property_ids.filter((x) => x !== pid) : [...f.property_ids, pid],
  }));

  // ---- Filters + export ----
  const [nameQ, setNameQ] = useState("");
  const [roleQ, setRoleQ] = useState("all");
  const filtered = useMemo(() => {
    const q = nameQ.trim().toLowerCase();
    return items.filter((u) => {
      if (roleQ !== "all" && u.role !== roleQ) return false;
      if (!q) return true;
      return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    });
  }, [items, nameQ, roleQ]);
  const clearFilters = () => { setNameQ(""); setRoleQ("all"); };
  const hasFilters = nameQ || roleQ !== "all";

  const exportColumns = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "role_label", label: "Role" },
    { key: "properties_label", label: "Property access" },
    { key: "can_access_pos_label", label: "POS access" },
    { key: "auth_provider", label: "Auth" },
    { key: "joined", label: "Joined" },
  ];
  const buildExportRows = () => filtered.map((u) => {
    const propNames = (u.property_ids || []).length
      ? (u.property_ids || []).map((pid) => properties.find((p) => p.id === pid)?.name || pid).join(", ")
      : "All properties";
    return {
      name: u.name,
      email: u.email,
      role_label: roleLabel[u.role] || u.role,
      properties_label: propNames,
      can_access_pos_label: u.can_access_pos === false ? "No" : "Yes",
      auth_provider: u.auth_provider || "email",
      joined: (u.created_at || "").slice(0, 10),
    };
  });
  const exportXlsx = () => {
    downloadRowsAsXlsx({ rows: buildExportRows(), columns: exportColumns, filename: "staff", sheetName: "Staff" });
    toast.success(`Exported ${filtered.length} staff to Excel`);
  };
  const printList = () => {
    const html = rowsToHtmlTable({ rows: buildExportRows(), columns: exportColumns });
    const filterNote = hasFilters ? `Filtered · ${filtered.length} of ${items.length}` : `All staff · ${items.length}`;
    openPrintWindow({ title: "Staff & Access", subtitle: filterNote, bodyHtml: html });
  };

  return (
    <div>
      <PageHeader
        overline="Team"
        title="Staff & Access"
        description="Manage roles, add new team members, and lock access to specific hotels."
        actions={isAdmin && (
          <Dialog open={newOpen} onOpenChange={setNewOpen}>
            <DialogTrigger asChild>
              <Button data-testid="staff-new-btn"><UserPlusIcon size={16} className="mr-1" />Add staff</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New team member</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="staff-new-name" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="staff-new-email" /></div>
                <div><Label>Temporary password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="staff-new-password" /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="staff-new-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="front_desk">Front Desk</SelectItem>
                      <SelectItem value="housekeeping">Housekeeping</SelectItem>
                      <SelectItem value="restaurant_manager">Restaurant Manager (POS only)</SelectItem>
                      <SelectItem value="service">Service / Waiter (take orders)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.role !== "admin" && properties.length > 0 && (
                  <div>
                    <Label>Property access <span className="text-xs text-muted-foreground font-normal">(empty = all)</span></Label>
                    <div className="mt-2 max-h-40 overflow-y-auto space-y-1 rounded-md border border-border p-2">
                      {properties.map((p) => (
                        <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded p-1" data-testid={`staff-new-prop-${p.id}`}>
                          <Checkbox checked={form.property_ids.includes(p.id)} onCheckedChange={() => toggleAssign(p.id)} />
                          <span className="flex-1 truncate">{p.name}</span>
                          <span className="text-xs text-muted-foreground">{p.city}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <DialogFooter><Button type="submit" data-testid="staff-new-submit">Create staff</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      />

      <Card className="p-3 mb-4 flex flex-wrap gap-2 items-center" data-testid="staff-filter-bar">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or email…"
            value={nameQ}
            onChange={(e) => setNameQ(e.target.value)}
            className="pl-9"
            data-testid="staff-search"
          />
        </div>
        <Select value={roleQ} onValueChange={setRoleQ}>
          <SelectTrigger className="w-48" data-testid="staff-role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {Object.entries(roleLabel).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button type="button" variant="ghost" size="sm" onClick={clearFilters} data-testid="staff-clear-filters"><XIcon size={12} className="mr-1" />Clear</Button>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <Button variant="outline" size="sm" onClick={printList} data-testid="staff-print"><PrinterIcon size={14} className="mr-1" />Print</Button>
          <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="staff-export-xlsx"><FileXlsIcon size={14} className="mr-1" />Excel</Button>
          <span className="text-xs text-muted-foreground" data-testid="staff-result-count">{filtered.length} of {items.length}</span>
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Person</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Property access</TableHead>
              <TableHead>POS access</TableHead>
              <TableHead>Section permissions</TableHead>
              <TableHead>Auth</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => {
              const isMaster = isMasterUser(u);
              return (
              <TableRow key={u.id} data-testid={`staff-row-${u.id}`} className={isMaster ? "bg-gold/5" : ""}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8"><AvatarImage src={u.avatar} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
                    <div className="flex flex-col">
                      <span className="font-medium flex items-center gap-1">{u.name}{isMaster && <CrownSimpleIcon size={12} weight="fill" className="text-gold" />}</span>
                      {isMaster && <span className="text-[9px] uppercase tracking-widest text-gold" data-testid={`staff-master-badge-${u.id}`}>Master owner · immutable</span>}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  {isMaster ? (
                    <Badge variant="outline" className="bg-gold/15 text-gold border-gold/40 flex items-center gap-1 w-fit" data-testid={`staff-role-locked-${u.id}`}><LockKeyIcon size={10} weight="fill" /> Admin · locked</Badge>
                  ) : isAdmin ? (
                    <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                      <SelectTrigger className="w-40 h-8" data-testid={`staff-role-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="front_desk">Front Desk</SelectItem>
                        <SelectItem value="housekeeping">Housekeeping</SelectItem>
                        <SelectItem value="restaurant_manager">Restaurant Manager (POS only)</SelectItem>
                        <SelectItem value="service">Service / Waiter</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className={roleColor[u.role]}>{roleLabel[u.role]}</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {isMaster ? (
                    <span className="text-xs flex items-center gap-1 text-gold" data-testid={`staff-assign-locked-${u.id}`}><InfinityIcon size={11} /> All properties</span>
                  ) : (
                    <PropertyRoles
                      user={u}
                      properties={properties}
                      roleLabel={roleLabel}
                      allowAdmin={!!masterEmail && (user?.email || "").toLowerCase() === masterEmail}
                      onSave={(pr) => setPropertyRoles(u, pr)}
                      disabled={!isAdmin || (u.role === "admin" && (user?.email || "").toLowerCase() !== masterEmail)}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {isMaster ? (
                    <span className="text-xs flex items-center gap-1 text-gold" data-testid={`staff-pos-locked-${u.id}`}><InfinityIcon size={11} /> Always on</span>
                  ) : (
                    <PosAccessToggle user={u} disabled={!isAdmin} onSave={async (v) => {
                      await api.patch(`/users/${u.id}`, { can_access_pos: v });
                      toast.success(v ? "POS access granted" : "POS access revoked");
                      load();
                    }} />
                  )}
                </TableCell>
                <TableCell>
                  <PermissionsButton user={u} disabled={!isAdmin} onSaved={load} locked={isMaster} />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground capitalize">{u.auth_provider}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{u.created_at?.slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  {isMaster ? (
                    <span className="text-[10px] uppercase tracking-widest text-gold flex items-center gap-1 justify-end" title="Transfer master ownership from Admin Console → Master to remove"><LockKeyIcon size={11} weight="fill" /> Protected</span>
                  ) : isAdmin && u.id !== user.id ? (
                    <Button size="icon" variant="ghost" onClick={() => remove(u)} data-testid={`staff-remove-${u.id}`}><TrashIcon size={14} /></Button>
                  ) : null}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
        Each person can have a <strong>different role at each property</strong> (including Admin), and only sees the properties assigned to them. Staff with no assigned property use their main role at every property. <br />
        <strong className="text-gold">The master owner</strong> is immutable: their role, permissions, POS access and property scope cannot be edited and their account cannot be deleted. To reassign ownership, go to <em>Admin Console → Master → Transfer ownership</em>.
      </p>
    </div>
  );
}
